import { ACCOUNTLOCKKIND, AccountLockKind } from "../data/constants";
import { USERSIGNINMESSAGES } from "../constants/messages";

/**
 * Maps the API's isAdminLocked flag (sent alongside result "Account Locked") to the lock reason.
 *
 * The flag is optional on the DTO, so an older/partial API response leaves it undefined. That is
 * deliberately NOT treated as "not admin locked": telling an admin-locked user that resetting
 * their password will unlock the account would be a false promise, so an unknown reason falls
 * back to the neutral message instead.
 */
export const resolveAccountLockKind = (isAdminLocked?: boolean): AccountLockKind => {
  if (isAdminLocked === true) return ACCOUNTLOCKKIND.ADMIN;
  if (isAdminLocked === false) return ACCOUNTLOCKKIND.FAILED_ATTEMPTS;
  return ACCOUNTLOCKKIND.UNSPECIFIED;
};

/** The alert copy for a locked account, chosen by why it is locked. */
export const getAccountLockMessage = (lockKind: AccountLockKind): string => {
  if (lockKind === ACCOUNTLOCKKIND.ADMIN) {
    return USERSIGNINMESSAGES.ACCOUNT_ADMIN_LOCKED_MESSAGE;
  }
  if (lockKind === ACCOUNTLOCKKIND.FAILED_ATTEMPTS) {
    return USERSIGNINMESSAGES.ACCOUNT_LOCKED_RESET_ALLOWED_MESSAGE;
  }
  return USERSIGNINMESSAGES.ACCOUNT_LOCKED_MESSAGE;
};

/**
 * The alert copy for a locked account inside the Forgot/Reset Password dialog.
 *
 * Differs from getAccountLockMessage because the user is already in the reset flow: pointing them
 * at the reset link would be nonsense here. A failed-attempt lockout is expected to be let through
 * by the API (it returns "Valid User" for the RESET-PASSWORD verification type), so reaching this
 * with anything other than an admin lock means the API blocked it for an unstated reason - show the
 * neutral message rather than guidance we cannot back up.
 */
export const getResetFlowLockMessage = (lockKind: AccountLockKind): string =>
  lockKind === ACCOUNTLOCKKIND.ADMIN
    ? USERSIGNINMESSAGES.ACCOUNT_ADMIN_LOCKED_MESSAGE
    : USERSIGNINMESSAGES.ACCOUNT_LOCKED_MESSAGE;

/**
 * The failed-password message, with the attempts-remaining warning appended when the API told us
 * how many are left. remainingAttempts is MaxUserFailedLoginAttemptCount minus the current
 * FailedLoginAttemptCount; the max is a server-side setting, so it cannot be computed here.
 * Values of 0 or below are dropped — at that point the account is locked and the lock message
 * takes over, so promising "0 attempts remaining" would only confuse.
 */
export const buildIncorrectPasswordMessage = (remainingAttempts?: number): string => {
  if (remainingAttempts === undefined || remainingAttempts === null || remainingAttempts <= 0) {
    return USERSIGNINMESSAGES.INCORRECT_PASSWORD_MESSAGE;
  }
  const attemptsWarning = USERSIGNINMESSAGES.ATTEMPTS_REMAINING_MESSAGE.replace(
    "{number}",
    remainingAttempts.toString(),
  );
  return `${USERSIGNINMESSAGES.INCORRECT_PASSWORD_MESSAGE} ${attemptsWarning}`;
};
